import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Timer,
  Play,
  Pause,
  Square,
  Zap,
  Clock,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface TimerLog {
  id: string;
  created_at: string;
  action: string;
  accumulated_seconds: number;
  user: { id: string; full_name: string; avatar_url?: string | null };
  task: { id: string; reference_id: string | null; title: string };
}

interface TimerLogsResponse {
  data: TimerLog[];
  pagination: { cursor: string | null; has_more: boolean; total_count: number };
}

interface UsersResponse {
  data: Array<{ id: string; full_name: string }>;
}

const actionConfig: Record<string, { icon: typeof Play; label: string; className: string }> = {
  start: { icon: Play, label: 'Start', className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
  pause: { icon: Pause, label: 'Pause', className: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
  stop: { icon: Square, label: 'Stop', className: 'bg-red-500/10 text-red-500 border-red-500/20' },
  auto_start: { icon: Zap, label: 'Auto Start', className: 'bg-teal-500/10 text-teal-500 border-teal-500/20' },
  auto_pause: { icon: Clock, label: 'Auto Pause', className: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
};

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function TimerLogsPage() {
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (userId !== 'all') params.user_id = userId;
  if (actionFilter !== 'all') params.action = actionFilter;
  if (cursor) params.cursor = cursor;

  const { data, isLoading } = useQuery<TimerLogsResponse>({
    queryKey: ['admin-timer-logs', params],
    queryFn: () => api.get('/admin/timer-logs', { params }).then((r) => r.data),
  });

  const { data: usersData } = useQuery<UsersResponse>({
    queryKey: ['admin-users-list'],
    queryFn: () => api.get('/admin/users', { params: { limit: '100' } }).then((r) => r.data),
  });

  const logs = data?.data ?? [];
  const pagination = data?.pagination;
  const users = usersData?.data ?? [];

  function resetPagination() {
    setCursor(undefined);
    setCursorStack([]);
  }

  function goNext() {
    if (pagination?.cursor) {
      setCursorStack((s) => [...s, cursor ?? '']);
      setCursor(pagination.cursor);
    }
  }

  function goPrev() {
    const stack = [...cursorStack];
    const prev = stack.pop();
    setCursorStack(stack);
    setCursor(prev || undefined);
  }

  function formatTimestamp(d: string) {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Timer Activity Logs"
        description="Track and debug timer activities across all users"
      />

      <div className="flex flex-col gap-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by task or user..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); resetPagination(); }}
              className="pl-9"
            />
          </div>

          <Select value={userId} onValueChange={(v) => { setUserId(v); resetPagination(); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Action Badges */}
        <div className="flex flex-wrap gap-2">
          <Badge
            variant={actionFilter === 'all' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => { setActionFilter('all'); resetPagination(); }}
          >
            All Actions
          </Badge>
          {Object.entries(actionConfig).map(([action, config]) => {
            const Icon = config.icon;
            return (
              <Badge
                key={action}
                variant="outline"
                className={cn(
                  'cursor-pointer gap-1',
                  actionFilter === action && config.className,
                )}
                onClick={() => { setActionFilter(action); resetPagination(); }}
              >
                <Icon className="h-3 w-3" />
                {config.label}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div>
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Accumulated Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No timer logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const config = actionConfig[log.action];
                  const Icon = config?.icon || Timer;
                  return (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-sm">
                        {formatTimestamp(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('gap-1', config?.className)}>
                          <Icon className="h-3 w-3" />
                          {config?.label || log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <UserAvatar
                            name={log.user.full_name}
                            avatarUrl={log.user.avatar_url}
                            size="xs"
                          />
                          <span className="text-sm">{log.user.full_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm text-primary">
                          {log.task.reference_id || log.task.title}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatDuration(log.accumulated_seconds)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {pagination ? `${pagination.total_count} total entries` : ''}
          {pagination?.has_more && ' (more available)'}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goPrev}
            disabled={cursorStack.length === 0}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goNext}
            disabled={!pagination?.has_more}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
